// Configuración de la completitud por nivel (base, empresa, cliente, área de
// negocio): qué datos y documentos cuentan para el porcentaje de cada
// postulación. Gana el nivel más específico; lo no configurado hereda.
//
//   GET  {APPLICANT}/application/completeness-config-scopes?userId=<id>
//   → { options }   empresas, clientes y áreas configurables
//   GET  {APPLICANT}/application/completeness-config?userId=<id>&scope=&scopeId=
//   → IConfiguracionCompletitud
//   POST {APPLICANT}/application/completeness-config?userId=<id>
//        { scope, scopeId, modes: { CAMPO: modo } }  → { message, ...IConfiguracionCompletitud }
//
// Solo los perfiles adm_tw y adm_ti ven la pantalla; el backend vuelve a validar.

import { readApiErrorMessage } from "../components/pages/Postulaciones/postulacionesUtils";

export type AlcanceCompletitud = "BASE" | "EMPRESA" | "CLIENTE" | "AREA_NEGOCIO";
export type ModoCompletitud = "OBLIGATORIO" | "SEGUN_FORMULARIO" | "NO_APLICA" | "HEREDA";
export type OrigenModo = AlcanceCompletitud | "DEFECTO";

export interface IOpcionAlcance {
  scope: Exclude<AlcanceCompletitud, "BASE">;
  id: string;
  name: string;
  /** Cliente del área de negocio. */
  parentId: string | null;
  /** Reglas propias configuradas. */
  rules: number;
}

export interface ICampoCompletitud {
  key: string;
  label: string;
  group: "IDENTIDAD" | "CONTACTO" | "FORMULARIO" | "DOCUMENTOS";
  defaultMode: ModoCompletitud;
  modes: ModoCompletitud[];
  /**
   * Modo que guarda el switch "Se valida" encendido: OBLIGATORIO en datos fijos,
   * SEGUN_FORMULARIO en los que pide el formulario. Opcional por compatibilidad
   * con un backend anterior (se deduce de `modes`).
   */
  validatedMode?: ModoCompletitud;
}

export interface IConfiguracionCompletitud {
  scope: AlcanceCompletitud;
  scopeId: string;
  fields: ICampoCompletitud[];
  /** Reglas propias de este nivel (incluye HEREDA). */
  own: Record<string, ModoCompletitud>;
  /** Lo que valdría cada campo sin reglas propias, y de qué nivel viene. */
  inherited: Record<string, { mode: ModoCompletitud; source: OrigenModo }>;
}

const buildUrl = (resource: string, params: Record<string, string> = {}): string => {
  const baseUrl = process.env.REACT_APP_API_APPLICANT;
  if (!baseUrl) throw new Error("Configuración faltante: REACT_APP_API_APPLICANT.");
  const query = new URLSearchParams({ userId: sessionStorage.getItem("usuarioId") ?? "", ...params });
  return `${baseUrl}/application/${resource}?${query.toString()}`;
};

export const getOpcionesAlcance = async (): Promise<IOpcionAlcance[]> => {
  const res = await fetch(buildUrl("completeness-config-scopes"));
  if (!res.ok) throw new Error(await readApiErrorMessage(res));
  const body = (await res.json()) as { options?: IOpcionAlcance[] };
  return body.options ?? [];
};

export const getConfiguracionCompletitud = async (
  scope: AlcanceCompletitud,
  scopeId: string,
): Promise<IConfiguracionCompletitud> => {
  const res = await fetch(buildUrl("completeness-config", { scope, scopeId }));
  if (!res.ok) throw new Error(await readApiErrorMessage(res));
  return (await res.json()) as IConfiguracionCompletitud;
};

export const guardarConfiguracionCompletitud = async (
  scope: AlcanceCompletitud,
  scopeId: string,
  modes: Record<string, ModoCompletitud>,
): Promise<IConfiguracionCompletitud & { message: string }> => {
  const res = await fetch(buildUrl("completeness-config"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ scope, scopeId, modes }),
  });
  if (!res.ok) throw new Error(await readApiErrorMessage(res));
  return (await res.json()) as IConfiguracionCompletitud & { message: string };
};
